/**
 * The deal's reference facts: who the customer is and what the deal is.
 *
 * The Customer block sits directly under the header in the tray and on the page, always open: its "Customer"
 * label with the customer's ServiceTitan link and the Customer 360 link on its right edge, the name, full-size
 * Call/Text/Email/Directions buttons (one row per value), the phone, email and address, and their AI profile as a
 * plain two-line paragraph with no leaf or label, More only when it overflows (John Lee, 2026-10-31). Extra numbers
 * fold under it. The customer's other open deals are records of their own, in Other Deals right after it
 * (`OtherDealsBlock`, John Lee, 2026-11-02). There is no
 * last-touch line: Next steps and the timeline carry that. Neither block carries a colored top rule — color on this page means status.
 *
 * The Deal card stays editable: Name, Value, Owner (editable), then Linked records, then Type, Origin, Created.
 * Linked records' own status is typed from the stage line's records; lost Pipedrive deals are summed up in one
 * line and a value that disagrees with the estimate says so inline. The tray folds it to one summary line; the
 * page opens it. A row that has nothing to say is omitted rather than dashed, except the value, which renders its
 * absence on purpose: that row is where the operator sets one.
 *
 * Placement (the page's sidebar beside the work, or the tray's one column) is the parent's.
 */

import { Pencil } from "lucide-react";
import { useState, type ReactNode, type Ref } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { StCustomerChip } from "@/components/assign/st-customer-chip";
import { customer360Href } from "@/components/records/customer-360";
import { OPERATION_TYPE_CONFIG } from "@/components/records/operation-type-badge";
import { RecordCustomerBlock } from "@/components/records/record-customer-block";
import { useRecordHeaderBrand } from "@/components/records/record-brand";
import { RecordOtherDeals } from "@/components/records/record-other-deals";
import { RecordLink } from "@/components/records/record-link";
import { firstNameOf } from "@/components/records/record-names";
import { RecordReachRow } from "@/components/records/record-reach-row";
import { FactRow, RecordSection } from "@/components/records/record-section";
import { LeadAssigneePopover } from "@/components/lead-command-center/LeadAssigneePopover";
import { LeadOperationTypeEditPopover } from "@/components/lead-command-center/LeadOperationTypeEditPopover";
import { LeadOriginEditPopover } from "@/components/lead-command-center/LeadOriginEditPopover";
import { OwnerAvatar } from "@/components/people/owner-avatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResponsiveOverlay } from "@/components/ui/responsive-overlay";
import { useCapabilities } from "@/hooks/use-capabilities";
import { useOptionalAuth } from "@/contexts/auth-context";
import { formatBusinessDate } from "@/lib/business-date";
import { cn } from "@/lib/utils";
import type { DealDetail, DealHolding, DealStageLineRecords } from "@/hooks/use-deal-board";
import { usePipedriveFrozen } from "@/hooks/use-brands";
import { useStUrls } from "@/hooks/use-st-urls";
import { crmSourceHref } from "@/lib/crm-links";
import { holdRowId } from "@/components/records/record-reference-badge";
import { formatBusinessTime, formatBusinessWeekdayDay } from "@/lib/business-date";
import { htmlToText } from "@/lib/sop-summary";

import { dealHeaderSources } from "./deal-card";
import { useDealEmailCompose } from "./deal-email-composer";
import { DealTitleEditor } from "./deal-title-editor";
import { LinkPipedriveDealDialog, UnlinkPipedriveDealButton } from "./link-pipedrive-deal-dialog";
import { decidingEstimate } from "./deal-stage-line";
import { formatDealValue } from "./deal-value";
import { DealValueEditor } from "./deal-value-editor";
import { OwnerSourceMark } from "./owner-source-mark";
import { StCustomerDialog } from "./st-customer-dialog";
import { INDENT, T_META } from "@/components/records/visual-system";

/** The deal's customer as the reach row reads them, in the tray (the header's row) or the Customer block. */
function DealReach({
  deal,
  customerHref,
  heading,
  gridButtons,
  className,
}: {
  deal: DealDetail;
  customerHref?: string | null;
  heading?: ReactNode;
  gridButtons?: boolean;
  className?: string;
}) {
  // Email opens the deal's own composer, at the top of the Timeline, for anyone who may send deal email.
  const compose = useDealEmailCompose();
  return (
    <RecordReachRow
      phone={deal.person_phone}
      phones={deal.person_phones}
      email={deal.person_email}
      address={deal.location}
      customerHref={customerHref}
      personId={deal.person_id}
      tenantId={deal.tenant_id}
      firstName={firstNameOf(deal.person_name)}
      heading={heading}
      onEmail={compose?.openNew}
      gridButtons={gridButtons}
      className={className}
    />
  );
}

/** The customer's ServiceTitan record on the Customer heading, beside Customer 360 →, the way Customer 360 shows its
 *  source links (John Lee, 2026-11-02): the dim source mark is the link. Pending and failed keep `StCustomerChip`'s
 *  own words in the same spot (there is no link yet either way). */
function CustomerStHead({ deal }: { deal: DealDetail }) {
  const stUrls = useStUrls();
  if (deal.st_customer_pending || deal.st_handoff_failed || !deal.st_customer_id) {
    return (
      <StCustomerChip
        tenantId={deal.tenant_id}
        stCustomerId={deal.st_customer_id ?? null}
        pending={deal.st_customer_pending}
        failed={deal.st_handoff_failed}
      />
    );
  }
  const href = crmSourceHref(
    { source_system: "servicetitan", source_entity_type: "customer", source_entity_id: deal.st_customer_id },
    stUrls,
    deal.tenant_id,
  );
  return href ? (
    <span data-testid="deal-customer-st">
      <RecordLink link={{ label: "ServiceTitan customer", href, id: deal.st_customer_id, system: "servicetitan" }} dim />
    </span>
  ) : null;
}

/** A customer's extra phone, in the EXACT reach-row shape the primary one uses — value, Call/Text on the
 *  right (John Lee, round 4: "not a different/denser layout for the extra ones"). */
function ExtraPhoneRow({ deal, phone }: { deal: DealDetail; phone: string }) {
  const compose = useDealEmailCompose();
  return (
    <RecordReachRow
      phone={phone}
      personId={deal.person_id}
      tenantId={deal.tenant_id}
      firstName={firstNameOf(deal.person_name)}
      onEmail={compose?.openNew}
      density="block"
    />
  );
}

export function CustomerBlock({
  deal,
  gridButtons = false,
  className,
}: {
  deal: DealDetail;
  gridButtons?: boolean;
  className?: string;
}) {
  const { can } = useCapabilities();
  // The Customer 360 page is its own capability, and the name links there only for someone the route lets in.
  const customerHref = customer360Href(deal.person_id, { canViewCustomers: can("customers.view") });
  // Every OTHER on-file number besides the primary — each gets its own full reach row once expanded.
  const extraPhones = (deal.person_phones ?? [])
    .map((p) => p.value)
    .filter((v) => v && v !== deal.person_phone);
  const hasStCustomerFact = deal.st_customer_pending || deal.st_handoff_failed || !!deal.st_customer_id;
  return (
    <RecordCustomerBlock
      name={deal.person_name}
      customerHref={customerHref}
      headEnd={hasStCustomerFact ? <CustomerStHead deal={deal} /> : null}
      summary={deal.person_summary}
      renderReach={(heading) => (
        <DealReach deal={deal} customerHref={customerHref} heading={heading} gridButtons={gridButtons} />
      )}
      // Their other numbers, folded behind the same disclosure row Details uses; no fold when there are none.
      more={
        extraPhones.length > 0
          ? {
              summary: `${extraPhones.length} more ${extraPhones.length === 1 ? "number" : "numbers"}`,
              content: (
                <>
                  {extraPhones.map((phone) => (
                    <ExtraPhoneRow key={phone} deal={deal} phone={phone} />
                  ))}
                </>
              ),
            }
          : null
      }
      className={className}
      testId="deal-customer-block"
    />
  );
}

/** Under the deal's name: its value, then the owner — only when the owner is not the viewer themself, "Owned by
 *  <name>" or "No owner" (John Lee, 2026-10-31). ONE text run that truncates as a unit on a narrow header. The brand
 *  sits on the name's own line (`RecordName`, John Lee, 2026-11-02), the way every record header shows it. */
export function DealHeaderLine({
  deal,
  onEdit,
}: {
  deal: DealDetail;
  /** Opens "Edit deal" (name/value/owner, round 4): a pencil that reveals on hover/focus of this row,
   *  desktop only — every device also reaches the same dialog from the header's "⋯" menu. Omitted where
   *  the caller has nowhere to open it (none today; kept optional for a header with no menu). */
  onEdit?: () => void;
}) {
  const value = formatDealValue(deal.value_amount, deal.value_currency);
  const viewer = useOptionalAuth()?.user ?? null;
  const ownerIsViewer = !!viewer?.id && deal.owner_user_id === viewer.id;
  const ownerWords = ownerIsViewer
    ? null
    : deal.owner_user_id
      ? `Owned by ${deal.owner_user_name ?? "someone"}`
      : "No owner";
  return (
    <div className={cn("group/header-line flex min-w-0 items-center gap-1.5", T_META)} data-testid="deal-header-line">
      <span className="min-w-0 truncate">
        <span className={cn("tabular-nums", value && "font-medium text-foreground")}>{value ?? "No value"}</span>
        {ownerWords ? <> · {ownerWords}</> : null}
      </span>
      {/* After the badge, so the pencil, invisible until hover, never opens a gap between the owner and the badge.
          `size-5`, not `h-5 w-5`: `size="icon"` is `size-9`, which `h-5 w-5` does not override. */}
      {onEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Edit deal"
          className="hidden size-5 shrink-0 text-muted-foreground opacity-0 sm:inline-flex group-hover/header-line:opacity-100 group-focus-within/header-line:opacity-100 focus-visible:opacity-100"
        >
          <Pencil className="size-3" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

/** Name, Value and Owner, in one small dialog — relocated here from Details (John Lee, round 4): the
 *  header shows them, so editing opens from the header's pencils or its "⋯" menu's "Edit deal", on every
 *  device. Each field keeps its own existing editor and its own capability gate (a viewer who may not
 *  rename or assign sees that field read-only, same as it always has). A short edit of one thing, so
 *  `ResponsiveOverlay kind="dialog"`: centered on tablet and desktop, the shared bottom sheet on a phone. */
export function DealEditDialog({ deal, open, onOpenChange }: { deal: DealDetail; open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <ResponsiveOverlay kind="dialog" open={open} onOpenChange={onOpenChange} title="Edit Deal">
      <dl className="space-y-1.5">
        <FactRow label="Name">
          <DealTitleEditor deal={deal} />
        </FactRow>
        <FactRow label="Value">
          <DealValueEditor deal={deal} />
        </FactRow>
        <FactRow label="Owner">
          <DealOwnerRow deal={deal} />
        </FactRow>
      </dl>
    </ResponsiveOverlay>
  );
}

/** The owner as the Owner row draws them: initials circle, name, and the source's mark. */
function OwnerName({ deal }: { deal: DealDetail }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-foreground">
      <OwnerAvatar name={deal.owner_user_name} size="sm" />
      <span className="min-w-0 truncate text-sm">{deal.owner_user_name ?? "Owner"}</span>
      <OwnerSourceMark source={deal.owner_source} />
    </span>
  );
}

/** The Owner row, compact (decided by John Lee, 2026-10-25): initials circle and the name, which gets the room and
 *  shows in full when it fits, then the source's small mark (Pipedrive's, or an "ST" badge) and the pencil the row's
 *  other fields use. The server decides who is shown and why (`owner_user_id` / `owner_source`); a DCC assignee shows
 *  only the pencil, and with nobody shown the row is the shared "+ Assign". */
function DealOwnerRow({ deal }: { deal: DealDetail }) {
  const { can } = useCapabilities();
  // Assigning is the board's rep-verb route (`deal_management.triage`); without it the
  // owner is shown, not offered for change.
  const canAssign = can("deal_management.triage");
  const popover = (triggerStyle?: "pencil") => (
    <LeadAssigneePopover
      leadId={deal.id}
      tenantId={deal.tenant_id}
      assignedUserId={deal.assigned_to_user_id}
      assignedDisplayName={deal.assigned_to_user_name}
      assignedEmail={null}
      triggerStyle={triggerStyle}
      hasAddress={deal.location != null}
      hasStCustomer={!!deal.st_customer_id}
      // Only the DCC passes this: on a deal whose ServiceTitan project the owner is
      // mirrored onto, clearing the owner clears the project's manager too, so Unassign
      // asks first. A deal with no project, or a brand the dual write is off for, gets
      // today's unconfirmed Unassign.
      stManagerSync={
        deal.st_project_id && deal.st_project_status === "created"
          ? { st_project_id: deal.st_project_id, manager_name: deal.assigned_to_user_name }
          : undefined
      }
    />
  );

  if (!deal.owner_user_id) {
    return canAssign ? popover() : <span className="text-sm text-muted-foreground">Unassigned</span>;
  }
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <OwnerName deal={deal} />
      {canAssign ? popover("pencil") : null}
    </div>
  );
}

/** What a held record says about itself, in the operator's words: ServiceTitan's and survivorship's status
 *  vocabulary ("committed" is a sold estimate, "dismissed" a canceled job). */
const HOLDING_STATUS: Record<string, string> = {
  committed: "sold",
  dismissed: "canceled",
  lost: "dismissed",
  servicing: "in progress",
  survey_booked: "survey booked",
  proposal_issued: "proposal sent",
  converted: "won",
};

/** Held records shown before "Show N more". */
const HOLDINGS_SHOWN = 4;

/** A hold whose record already has its one outbound link in the tray header — tested by membership in
 *  `dealHeaderSources`. The tray header carries no outside links today (John Lee, 2026-10-31: row 1 holds
 *  only the name, Full Page and close), so `headerShown` is always false in practice and every hold keeps its
 *  own link, its only one — the mechanism stays wired for the header that once deduped against it. */
function isHeaderLinked(deal: DealDetail, h: DealHolding, headerShown: boolean): boolean {
  if (!headerShown) return false;
  return dealHeaderSources(deal).some(
    (s) =>
      s.source_system === h.source_system &&
      s.source_entity_type === h.source_entity_type &&
      String(s.source_entity_id) === String(h.source_entity_id),
  );
}

/** The records the deal holds, one line each: kind and number, then the record's own title and status. A
 *  record already linked out from the header (see `isHeaderLinked`) says its name as plain text — one link
 *  out per record, and the header carries it; everything else keeps its own outbound link, its only one.
 *  Each row carries the `id` a reference badge elsewhere in the tray (the stage explanation) scrolls to and
 *  flashes. Dead records (a Dismissed estimate, a Canceled job, a lost Pipedrive deal: the server's
 *  `is_terminal`) wait behind "Show N terminal"; sold, won and completed ones stay in the list. */
/** What a held record's own ServiceTitan row says, typed: an estimate's amount, a job's visit day and time. */
function holdingDetail(h: DealHolding, records: DealStageLineRecords | undefined): string | null {
  if (!records || h.source_system !== "servicetitan") return null;
  const id = String(h.source_entity_id);
  if (h.source_entity_type === "estimate") {
    const e = records.estimates.find((r) => r.record_id === id);
    return e ? formatDealValue(e.amount, "USD") : null;
  }
  if (h.source_entity_type === "job") {
    const visit = records.appointments.filter((r) => r.job_id === id && r.status !== "Canceled").at(-1);
    return visit?.start
      ? `visit ${formatBusinessWeekdayDay(visit.start)} ${formatBusinessTime(visit.start)}${visit.status ? ` · ${visit.status}` : ""}`
      : null;
  }
  return null;
}

/** The deal's value against the estimate the stage line speaks for, when they differ by a dollar or more. */
// eslint-disable-next-line react-refresh/only-export-components
export function valueMismatch(deal: DealDetail, records: DealStageLineRecords | undefined): string | null {
  const estimate = records ? decidingEstimate(records.estimates) : null;
  if (!estimate?.amount || !deal.value_amount || Math.abs(estimate.amount - deal.value_amount) < 1) return null;
  return `Deal value ${formatDealValue(deal.value_amount, deal.value_currency)} differs from estimate ${formatDealValue(estimate.amount, "USD")}`;
}

/** A terminal hold's own summary word, lowercase and muted: "dismissed", "canceled", "lost" — the same
 *  vocabulary `HOLDING_STATUS` already gives a live hold's line, just grouped into a count. */
function terminalSummaryWord(h: DealHolding): string {
  return h.status ? (HOLDING_STATUS[h.status] ?? h.status.replaceAll("_", " ")) : "closed";
}

/** The folded terminal line's words: "3 dismissed estimates", or the generic count when the group isn't
 *  one kind and one status (John Lee, round 4: "3 dismissed estimates · Show", never a listed row). */
function terminalSummary(terminal: DealHolding[]): string {
  const words = new Set(terminal.map(terminalSummaryWord));
  const kinds = new Set(terminal.map((h) => h.source_entity_type));
  if (words.size === 1 && kinds.size === 1) {
    const n = terminal.length;
    const kind = [...kinds][0];
    return `${n} ${[...words][0]} ${kind}${n === 1 ? "" : "s"}`;
  }
  return terminal.length === 1 ? "1 closed record" : `${terminal.length} closed records`;
}

/** The three link-management actions folded into one place (John Lee, round 4: "ONE 'Manage links' link
 *  at the bottom of that list") — Link Pipedrive deal, Link ServiceTitan work, and an Unlink per open
 *  Pipedrive hold. Reuses the existing dialogs verbatim; this popover only relocates their triggers. The
 *  ONLY link-colored text in the Linked records block — everything else there is plain foreground/muted
 *  text (John Lee, round 4). */
function ManageLinksPopover({
  deal,
  openPipedrive,
  canFixPipedrive,
  onLinkAnother,
  canFindStCustomer,
}: {
  deal: DealDetail;
  openPipedrive: DealHolding[];
  canFixPipedrive: boolean;
  onLinkAnother?: (() => void) | null;
  /** The deal has no ServiceTitan customer yet: offer to find one to link, or add one. */
  canFindStCustomer: boolean;
}) {
  const [linkingPipedrive, setLinkingPipedrive] = useState(false);
  const [findingStCustomer, setFindingStCustomer] = useState(false);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="link" size="sm" className="h-auto p-0 text-sm max-md:min-h-11">
          Manage Links
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <div className="flex flex-col">
          {canFixPipedrive ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto justify-start px-2 py-1.5 text-sm max-md:min-h-11"
              onClick={() => {
                setOpen(false);
                setLinkingPipedrive(true);
              }}
            >
              Link Pipedrive Deal
            </Button>
          ) : null}
          {onLinkAnother ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto justify-start px-2 py-1.5 text-sm max-md:min-h-11"
              onClick={() => {
                setOpen(false);
                onLinkAnother();
              }}
            >
              Link ServiceTitan Work
            </Button>
          ) : null}
          {canFindStCustomer ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto justify-start px-2 py-1.5 text-sm max-md:min-h-11"
              onClick={() => {
                setOpen(false);
                setFindingStCustomer(true);
              }}
            >
              Find or Add ServiceTitan Customer
            </Button>
          ) : null}
          {canFixPipedrive
            ? openPipedrive.map((h) => (
                <div key={h.source_entity_id} className="flex items-center justify-between gap-2 px-2 py-1.5">
                  <span className="min-w-0 truncate text-sm text-muted-foreground">
                    Unlink {htmlToText(h.title) || "Pipedrive deal"}
                  </span>
                  <UnlinkPipedriveDealButton dealId={deal.id} pdDealId={h.source_entity_id} />
                </div>
              ))
            : null}
        </div>
      </PopoverContent>
      <LinkPipedriveDealDialog
        target={linkingPipedrive ? { dealId: deal.id, dealLabel: htmlToText(deal.title) || "This deal" } : null}
        onOpenChange={(next) => setLinkingPipedrive(next)}
      />
      {/* Mounted per open, so each open mints a fresh link key. */}
      {findingStCustomer ? (
        <StCustomerDialog
          key={deal.id}
          target={{
            dealId: deal.id,
            dealLabel: htmlToText(deal.title) || deal.person_name || "This deal",
            hasAddress: deal.location_id !== null,
          }}
          onOpenChange={setFindingStCustomer}
        />
      ) : null}
    </Popover>
  );
}

function DealHoldings({
  deal,
  headerShown,
  records,
  onLinkAnother,
}: {
  deal: DealDetail;
  headerShown: boolean;
  records?: DealStageLineRecords;
  onLinkAnother?: (() => void) | null;
}) {
  const stUrls = useStUrls();
  const frozen = usePipedriveFrozen(deal.tenant_id);
  const { can } = useCapabilities();
  // Fixing the Pipedrive link: the "Link ServiceTitan work" people, and never on a brand that left
  // Pipedrive — there the links are the deal's only provenance, and unlinking would erase them.
  // Unknown (brands loading, or the fetch failed) offers nothing.
  const canFixPipedrive = can("deal_management.stage_move") && frozen === false;
  const [all, setAll] = useState(false);
  const [terminalShown, setTerminalShown] = useState(false);
  const holds = deal.holds ?? [];
  const open = holds.filter((h) => !h.is_terminal);
  const terminal = holds.filter((h) => h.is_terminal);
  const mismatch = valueMismatch(deal, records);
  // Open Pipedrive deals and the project come first (the server's order), so the fold only ever hides jobs and
  // estimates. Terminal records, whatever their kind, list after every open one.
  const shown = all ? open : open.slice(0, HOLDINGS_SHOWN);
  const line = (h: DealHolding) => {
    const key = `${h.source_system}:${h.source_entity_type}:${h.source_entity_id}`;
    const href = isHeaderLinked(deal, h, headerShown) ? null : crmSourceHref(h, stUrls, deal.tenant_id);
    const title = htmlToText(h.title);
    // The record's own kind, Title Case ("Job"), never the raw ServiceTitan title ("L20-0100 - MOMTAZ,
    // LILI") and never the id — both go only in the native hover title / the mark's own id tooltip
    // (John Lee, round 4).
    const kindWord = h.source_entity_type.charAt(0).toUpperCase() + h.source_entity_type.slice(1);
    const detail = holdingDetail(h, records);
    const label = detail ? `${kindWord} · ${detail}` : kindWord;
    const status = h.status ? (HOLDING_STATUS[h.status] ?? h.status.replaceAll("_", " ")) : null;
    return (
      <li key={key} id={holdRowId(key)} className="flex min-w-0 flex-wrap items-center gap-1 break-words rounded text-sm">
        <span className="text-foreground" title={title || undefined}>
          {label}
        </span>
        {status ? <span className="text-muted-foreground">· {status.toLowerCase()}</span> : null}
        {href ? (
          <RecordLink
            link={{ label: h.source_system === "pipedrive" ? "PD" : "ST", href, id: String(h.source_entity_id), system: h.source_system }}
            dim
          />
        ) : null}
      </li>
    );
  };
  // Every held Pipedrive deal, including a lost one: Unlink is the only way to remove a wrongly
  // linked one, and a lost deal is terminal (`open` excludes it) but still needs that action.
  const openPipedrive = holds.filter((h) => h.source_system === "pipedrive" && h.source_entity_type === "deal");
  // No ServiceTitan customer, none being added, no project to carry one: the rep may find or add it
  // (the "Link ServiceTitan work" people).
  const canFindStCustomer =
    can("deal_management.stage_move") &&
    !deal.st_customer_id &&
    !deal.st_customer_pending &&
    !deal.st_handoff_failed &&
    !deal.st_project_id;
  const showManageLinks = canFixPipedrive || !!onLinkAnother || canFindStCustomer;
  return (
    <ul className="space-y-1 text-sm" data-testid="deal-holdings">
      {shown.map(line)}
      {open.length > shown.length ? (
        <li>
          <Button variant="link" size="sm" className="h-auto p-0 text-sm" onClick={() => setAll(true)}>
            Show {open.length - shown.length} more
          </Button>
        </li>
      ) : null}
      {terminalShown ? terminal.map(line) : null}
      {terminal.length > 0 && !terminalShown ? (
        <li className="text-muted-foreground">
          {terminalSummary(terminal)}
          <span aria-hidden> · </span>
          <Button variant="link" size="sm" className="h-auto p-0 text-sm" onClick={() => setTerminalShown(true)}>
            Show
          </Button>
        </li>
      ) : null}
      {mismatch ? (
        <li className="text-warning" data-testid="deal-value-mismatch">
          {mismatch}
        </li>
      ) : null}
      {/* A viewer who may not manage links, on a deal with none, would otherwise see the label with nothing beside it. */}
      {holds.length === 0 && !showManageLinks ? <li className="text-muted-foreground">None yet</li> : null}
      {showManageLinks ? (
        <li>
          <ManageLinksPopover
            deal={deal}
            openPipedrive={openPipedrive}
            canFixPipedrive={canFixPipedrive}
            onLinkAnother={onLinkAnother}
            canFindStCustomer={canFindStCustomer}
          />
        </li>
      ) : null}
    </ul>
  );
}

/** The customer's other open deals as their own section, one record row each, each opening in the same panel. Every
 *  row names its brand when the header does (the header scope spans several families, or a sibling sits on another
 *  family), and says how that brand spells the customer when it differs beyond letter case. */
export function OtherDealsBlock({ deal, onOpenDeal }: { deal: DealDetail; onOpenDeal: (dealId: string) => void }) {
  const brandOf = useRecordHeaderBrand();
  const others = deal.other_open_deals ?? [];
  const sameName = (name: string | null | undefined) =>
    !name || !deal.person_name || name.trim().toLowerCase() === deal.person_name.trim().toLowerCase();
  return (
    <RecordOtherDeals
      titleId={`deal-${deal.id}-other-deals`}
      total={Math.max(deal.other_open_deal_count ?? 0, others.length)}
      onOpen={onOpenDeal}
      deals={others.map((d) => ({
        id: d.id,
        title: htmlToText(d.title) || "Untitled deal",
        amount: d.value_amount ? formatDealValue(d.value_amount, d.value_currency ?? null) : null,
        brand: brandOf(d.tenant_id, !!deal.other_open_deal_other_family),
        meta: [
          d.stage_title,
          d.owner_user_name ? `Owned by ${d.owner_user_name}` : null,
          sameName(d.person_name) ? null : `as “${d.person_name}”`,
        ],
      }))}
    />
  );
}

/** The deal's type, as the summary line and the Details editor both name it ("Sales", "Service", "Bid invite"). */
function effectiveOperationTypeLabel(deal: DealDetail): string | null {
  const type = deal.operation_type_override ?? deal.operation_type;
  return type ? OPERATION_TYPE_CONFIG[type]?.label ?? null : null;
}

/** The source a deal's records mostly come from, named the way a rep would say it. */
function primarySourceLabel(deal: DealDetail): string | null {
  const holds = deal.holds ?? [];
  if (holds.some((h) => h.source_system === "pipedrive")) return "Pipedrive";
  if (holds.some((h) => h.source_system === "servicetitan")) return "ServiceTitan";
  return null;
}

/** Details' one folded line: type, source, how many records it holds, and when it was created — a value
 *  mismatch, when there is one, leads it in the warning tone (John Lee, round 3 — "▸ Details  Sales · from
 *  Pipedrive · 4 linked records · created Sep 4"). */
function dealSummary(deal: DealDetail, records: DealStageLineRecords | undefined): ReactNode {
  const n = deal.holds?.length ?? 0;
  const type = effectiveOperationTypeLabel(deal);
  const source = primarySourceLabel(deal);
  const parts = [type, source ? `from ${source}` : null, n === 1 ? "1 linked record" : `${n} linked records`];
  if (deal.source_created_at) parts.push(`created ${formatBusinessDate(deal.source_created_at)}`);
  const line = parts.filter(Boolean).join(" · ");
  const mismatch = valueMismatch(deal, records);
  return mismatch ? (
    <>
      <span className="text-warning">{mismatch}</span>
      {line ? <> · {line}</> : null}
    </>
  ) : (
    line
  );
}

export function DealBlock({
  deal,
  records,
  onLinkAnother,
  onOpenDeal,
  linkInHeader = false,
  headerShown = false,
  open = true,
  onOpenChange,
  linkedRecordsRef,
  className,
}: {
  deal: DealDetail;
  /** The stage line's typed records: each linked record's typed status, and the value mismatch. */
  records?: DealStageLineRecords;
  onLinkAnother?: (() => void) | null;
  /** Opens one of the customer's other deals; without it the Other deals row is not shown. */
  onOpenDeal?: (dealId: string) => void;
  /** The record tray header carries the project link: this row keeps its sync state, warnings and "Link another". */
  linkInHeader?: boolean;
  /** A `RecordTrayHeader` sits above this body (the deal panel) and already carries every Pipedrive hold's and
   *  the linked ST project's one outbound link — the Linked records row drops its own. False (the default, and
   *  the standalone `/deals/:id` route's own value) when there is no header: every hold keeps its link, its only
   *  one, there. */
  headerShown?: boolean;
  /** Folded to one summary line (the tray's default) or open (the page's). */
  open?: boolean;
  /** With it, the title folds and unfolds the card. */
  onOpenChange?: (open: boolean) => void;
  /** The Linked records row, which the stage line scrolls to. */
  linkedRecordsRef?: Ref<HTMLDivElement>;
  className?: string;
}) {
  const queryClient = useQueryClient();
  // The overrides write through the LCC's hooks, which refresh the LCC's queues;
  // the deal page and the board column it sits in are told here.
  const refreshDeal = () => {
    void queryClient.invalidateQueries({ queryKey: ["dcc", "deal", deal.id] });
    void queryClient.invalidateQueries({ queryKey: ["dcc", "board"] });
  };
  void linkInHeader; // kept as a prop for API stability; the tray header carries no outside links today.
  void onOpenDeal; // "Other deals" moved to the Customer card (John Lee, round 4).

  // Details — the only remaining chevron on this page — is now folded by default EVERYWHERE, tray and
  // page alike (John Lee, round 4: two clean levels, not "open on the page, folded in the tray"). Exactly
  // three rows when it's open: Linked records, Type · Origin, Created. Name/Value/Owner moved to the
  // header (editable there — see `DealEditDialog`); ST project and ST customer are gone (the first
  // duplicated Linked records, the second moved to the Customer card); Other deals moved there too.
  return (
    <RecordSection
      titleId="deal-facts-heading"
      title="Details"
      open={open}
      onOpenChange={onOpenChange}
      summary={dealSummary(deal, records)}
      summaryTestId="deal-block-summary"
      className={className}
      testId="deal-block"
    >
      <div className={cn(INDENT, "space-y-1.5 pb-2.5")}>
        {/* Which deal this is: the records it holds, rendered even when it holds none, since "Manage
            links" lives there. Then one quiet line: type · origin · created (the busy label column is gone). */}
        <div ref={linkedRecordsRef} className="scroll-mt-4" data-testid="deal-block-records">
          <DealHoldings key={deal.id} deal={deal} headerShown={headerShown} records={records} onLinkAnother={onLinkAnother} />
        </div>
        <p className={cn(T_META, "flex flex-wrap items-center gap-x-1.5 gap-y-1")} data-testid="deal-block-facts">
          {/* Each popover gates its own edit on its capability and falls back to the read-only chip. */}
          <LeadOperationTypeEditPopover
            leadId={deal.id}
            autoOperationType={deal.operation_type}
            overrideOperationType={deal.operation_type_override ?? null}
            overridden={deal.operation_type_override != null}
            leadKind={{ auto: deal.lead_kind ?? null, override: deal.lead_kind_override ?? null }}
            emptyLabel="Untagged"
            onSettled={refreshDeal}
            plain
          />
          <span aria-hidden>·</span>
          <LeadOriginEditPopover
            leadId={deal.id}
            autoOrigin={deal.origin ?? null}
            autoIsAiListed={deal.is_ai_listed ?? false}
            overrideOrigin={deal.origin_override ?? null}
            overrideIsAiListed={deal.is_ai_listed_override ?? null}
            overridden={deal.origin_override != null || deal.is_ai_listed_override != null}
            onSettled={refreshDeal}
            plain
          />
          {deal.source_created_at ? (
            <>
              <span aria-hidden>·</span>
              <span className="tabular-nums">Created {formatBusinessDate(deal.source_created_at)}</span>
            </>
          ) : null}
        </p>
      </div>
    </RecordSection>
  );
}
